import { reAuthTabStatus, tabStatus } from "../constants/TabSessionStorage";
import { currentDate } from "../utils";
import { TabSessionDataReAuthResponse, TabSessionStorageResponse } from "./api/TabSessionStorage";

export interface IResidentialAddress {
  streetName1: string | null;
  streetName2: string | null;
  county: string | null;
  country: string;
  city: string | null;
  patientState: string | null;
  zipCode: string | null;
  latitude: number | null;
  longitude: number | null;
  addressTypeId: number;
}

export interface IServiceAddress extends IResidentialAddress {}

export interface IRequester {
  facilityTypeId: number;
  facilityName: string;
  requesterName: string;
  requesterExtension: string;
  requesterPhoneNo: string;
  requesterEmail: string;
  requesterFax: string;
  requesterNpi: string;
  streetName1: string;
  streetName2: string;
  county: string;
  city: string;
  requesterState: string;
  zipCode: string;
  facilityTypeName: string;
  serviceProviderId: number;
  agencyDetailsrequesterName?: string;
  sendApprovedAuthTo: number;
  secondaryEmailAddress: string;
}

export interface IReferralSource {
  facilityTypeId: string;
  facilityName: string;
  referralSourceName: string;
  referralSourceExtension: string | null;
  referralSourcePhoneNo: string;
  referralSourceEmail: string;
  referralSourceFax: string;
  ReferralSourceNpi: string;
  streetName1: string | null;
  streetName2: string | null;
  county: string | null;
  city: string | null;
  referralSourceState: string | null;
  zipCode: string | null;
  sameAsRequestingFacility: boolean;
  dischargeDate: string;
}

export interface IReferralDetail {
  patientId: number;
  receivedDateTime: string;
  urgencyId: number;
  receivedMethodId: number;
  receivedMethodName: string;
  isHomeBound: boolean | null;
  isSignedHealthOrder: string;
  signedOrderDate: string;
  signedOrderType: string;
  orderedById: number | null;
  acceptedById: number | null;
  isCustodialCare: string;
  isNonCoveredService: boolean | null;
  nonCoveredServices: string | null;
  healthProgramId: number | null;
  careProgramId: number | null;
  powerOfAttorney: string | null;
  urgencyStatus: string | null;
  orderedByName: string | null;
  acceptedByName: string | null;
  nonCoveredServiceNames: string | null;
  patientEligibilityId: number;
  fullNameCreated: string;
  authorizationTypeId: number;
  userCreated: string;
  userSpecificReceivedDateTime: string;
}

export interface IRequestIntake {
  referralDetail: IReferralDetail;
  residentialAddress: IResidentialAddress;
  serviceAddress: IServiceAddress;
  requester: IRequester;
  referralSource: IReferralSource;
  isSameAsResidential: boolean;
}

export interface IIcdCodesInfoDto {
  diagnosisTypeId: number;
  diagnosisCode: string;
  diagnosisCodeDescription: string;
  icdDiagnosisCodeId: number;
  refferralIcdCodeId: number;
}

export interface IIcdCodesDetails {
  typeId: number;
  fullNameCreated: string;
  icdCodesInfoDto: IIcdCodesInfoDto[];
}

export interface IPhysician {
  referralPhysicianDetailId: number;
  physicianId: number;
  physicianName: string;
  physicianNpiId: string;
  phoneExt: string;
  phoneNo: string;
  taxId: string;
  streetName1: string;
  streetName2: string;
  fax: string;
  county: string;
  city: string;
  physicianState: string;
  zipCode: string;
  physicianTypeId: number;
  userCreated?: string;
  physicianAddress: string;
  fullNameCreated: string;
}

export interface IReferralPhysicianDetail {
  primaryPhysician: PrimaryPhysicianDetails;
  orderingPhysician: OrderingPhysicianDetails;
}

export interface PrimaryPhysicianDetails extends IPhysician {
  physicianTypeId: number;
  latitude: number | null;
  longitude: number | null;
  sameAsOrdering: boolean;
}

export interface OrderingPhysicianDetails extends IPhysician {
  physicianTypeId: number;
  latitude: number | null;
  longitude: number | null;
  physicianSameAsPrimary: boolean;
}
export interface IReferralServiceProvider {
  referralServiceProviderId: number;
  serviceProviderId: number;
  providerName: string;
  providerNpi: string;
  reimbursementTypeId: number;
  reimbursementTypeName: string;
  streetName1: string;
  streetName2: string;
  providerCounty: string;
  providerCity: string;
  providerState: string;
  phone: string;
  extension: string;
  zipCode: string;
  fullNameCreated: string;
  providerAddress: string;
  providerId: number;
  plexisOfficeId: number;
}

export interface IReferralCareTypeDto {
  careTypeId: number;
}

export interface IReferralDisciplineDto {
  disciplineId: number;
  serviceNumber: number;
  acceptedVisits: number;
}

export interface IServiceRequestDetails {
  userCreated: string;
  startOfCare: string;
  referralCareTypeDto: IReferralCareTypeDto[];
  referralDisciplineDto: IReferralDisciplineDto[];
  diseasesDto: any;
  psychologicalDiseasesDto: any;
  fullNameCreated: string;
  isNurseToOpen: boolean;
  discipline: any;
}

export interface IAuthorizationDetail {
  requestIntake: IRequestIntake;
  icdCodesDetails: IIcdCodesDetails;
  referralPhysicianDetail: IReferralPhysicianDetail;
  referralServiceProvider: IReferralServiceProvider;
  serviceRequestDetails: IServiceRequestDetails;
}

export interface AuthDetails {
  requestId: string;
  userCreated: string;
  noteComment: string;
  dateCreated: string;
  authorizationDetail: IAuthorizationDetail;
}

export interface IDiagnosisCode {
  diagnosisCode: string;
  diagnosisCodeDescription: string;
  diagnosisTypeId: number;
  diagnosisType: string;
  icdDiagnosisCodeId: number;
}

export interface IServiceAddress {
  streetName1: string;
  streetName2: string;
  county: string;
  city: string;
  patientState: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  addressTypeId: number;
  country: string;
}

export interface ReferralSource {
  facilityTypeId: string;
  facilityName: string;
  referralSourceNpi: string;
  referralSourceName: string;
  referralSourcePhoneNo: string;
  referralSourceFax: string;
  streetName1: string;
  streetName2: string;
  county: string;
  city: string;
  referralSourceState: string;
  zipCode: string;
  referralSourceExtension: string;
  referralSourceEmail: string;
  serviceAddressDetails: IServiceAddress;
  facilityTypeName: string;
  sameAsRequestingFacility: boolean;
  dischargeDate: string;
  referralSourceTaxId: string;
}

export interface TabStatus {
  tabIndex: number;
  isVisited: boolean;
  label: string;
}

export interface TabData {
  agencyDetails: IRequester;
  physicianDetails: IReferralPhysicianDetail;
  diagnosisCode: IDiagnosisCode[];
  referralSource: ReferralSource;
  icdCodesDetails: IIcdCodesDetails;
  requestService: IServiceRequestDetails;
  referralServiceProvider: IReferralServiceProvider;
  documentUploads: any[];
  userCreated: string;
  requestId: string;
  referralIdCreated: string;
  noteComment: string;
  dateCreated: string;
  tabStatus: TabStatus[];
}
export const defaultPrimaryPhysician = {
  referralPhysicianDetailId: 0,
  physicianId: 0,
  physicianName: "",
  physicianNpiId: "",
  phoneExt: "",
  phoneNo: "",
  taxId: "",
  streetName1: "",
  streetName2: "",
  city: "",
  county: "",
  physicianState: "",
  zipCode: "",
  fax: "",
  latitude: null,
  longitude: null,
  physicianTypeId: 1,
  sameAsOrdering: false,
  physicianAddress: "",
  fullNameCreated: "",
};

export const defaultTabData: TabData = {
  agencyDetails: {
    facilityTypeId: 1,
    facilityName: "",
    requesterName: "",
    requesterExtension: "",
    requesterPhoneNo: "",
    requesterEmail: "",
    requesterFax: "",
    requesterNpi: "",
    streetName1: "",
    streetName2: "",
    county: "",
    city: "",
    requesterState: "",
    zipCode: "",
    facilityTypeName: "",
    agencyDetailsrequesterName: "",
    sendApprovedAuthTo: 0,
    serviceProviderId: 0,
    secondaryEmailAddress: "",
  },
  physicianDetails: {
    primaryPhysician: defaultPrimaryPhysician,
    orderingPhysician: {
      referralPhysicianDetailId: 0,
      physicianId: 0,
      physicianName: "",
      physicianNpiId: "",
      phoneExt: "",
      phoneNo: "",
      taxId: "",
      streetName1: "",
      streetName2: "",
      fax: "",
      county: "",
      city: "",
      physicianState: "",
      zipCode: "",
      physicianTypeId: 2,
      latitude: null,
      longitude: null,
      physicianAddress: "",
      userCreated: "",
      fullNameCreated: "",
      physicianSameAsPrimary: false,
    },
  },
  diagnosisCode: [],
  referralSource: {
    facilityTypeId: "",
    facilityName: "",
    referralSourceNpi: "",
    referralSourceName: "",
    referralSourcePhoneNo: "",
    referralSourceFax: "",
    streetName1: "",
    streetName2: "",
    county: "",
    city: "",
    referralSourceState: "",
    zipCode: "",
    referralSourceExtension: "",
    referralSourceEmail: "",
    serviceAddressDetails: {
      streetName1: "",
      streetName2: "",
      county: "",
      city: "",
      patientState: "",
      zipCode: "",
      latitude: 0,
      longitude: 0,
      addressTypeId: 2,
      country: "",
    },
    facilityTypeName: "",
    sameAsRequestingFacility: false,
    dischargeDate: "",
    referralSourceTaxId: "",
  },
  icdCodesDetails: {
    typeId: 0,
    fullNameCreated: "",
    icdCodesInfoDto: [],
  },
  requestService: {
    userCreated: "",
    startOfCare: "",
    referralCareTypeDto: [],
    referralDisciplineDto: [],
    diseasesDto: null,
    psychologicalDiseasesDto: null,
    fullNameCreated: "",
    isNurseToOpen: false,
    discipline: null,
  },
  referralServiceProvider: {
    referralServiceProviderId: 0,
    serviceProviderId: 0,
    providerName: "",
    providerNpi: "",
    streetName1: "",
    streetName2: "",
    providerCounty: "",
    providerCity: "",
    providerState: "",
    phone: "",
    extension: "",
    zipCode: "",
    fullNameCreated: "",
    providerAddress: "",
    providerId: 0,
    plexisOfficeId: 0,
    reimbursementTypeId: 0,
    reimbursementTypeName: "",
  },
  documentUploads: [],
  userCreated: "",
  requestId: "",
  referralIdCreated: "",
  noteComment: "",
  dateCreated: "",
  tabStatus: tabStatus,
};

export const notVisitedTabs = (tabSessionStorage: TabSessionStorageResponse) =>
  tabSessionStorage?.response?.tabStatus?.filter((tab) => !tab.isVisited) || [];

export const defaultReAuthTabData: ReAuthTabData = {
  requestId: "",
  userCreated: "",
  dateCreated: "",
  referralId: 0,
  noteComment: "",
  fullName: "",
  reAuthTabStatus: reAuthTabStatus,
  documentUploads: [],
  diagnosisCode: [],
  reAuthAgencyDetails: {
    facilityTypeId: 2,
    facilityName: "",
    requesterName: "",
    requesterExtension: "",
    requesterPhoneNo: "",
    requesterEmail: "",
    requesterFax: "",
    requesterNpi: "",
    streetName1: "",
    streetName2: "",
    county: "",
    city: "",
    requesterState: "",
    zipCode: "",
    facilityTypeName: "",
    agencyDetailsrequesterName: "",
    sendApprovedAuthTo: 0,
    serviceProviderId: 0,
    secondaryEmailAddress: "",
  },
  reAuthRequest: {
    referralId: 0,
    receivedDateTime: "",
    patientEligibilityId: 0,
    receivedMethodId: 0,
    urgencyId: 0,
    userNameCreated: "",
    userEmail: "",
    userPhone: "",
    facilityTypeId: 0,
    userCreated: "",
  },

  followingPhysicianDetail: {
    serviceRequestId: 0,
    referralId: 0,
    physicianName: "",
    physicianNpiId: "",
    phoneNo: "",
    county: "",
    zipCode: "",
    userNameCreated: "",
    referralPhysicianDetailId: 0,
    physicianId: 0,
    phoneExt: "",
    taxId: "",
    streetName1: "",
    streetName2: "",
    fax: "",
    city: "",
    physicianState: "",
    physicianTypeId: 0,
    physicianAddress: "",
    userCreated: "",
    fullNameCreated: "",
    physicianSameAsPrimary: false,
    physicianSameAsOrdering: false,
  },

  reAuthServiceRequest: {
    serviceRequestId: 0,
    referralId: 0,
    startOfCareDate: currentDate(),
    homeSN: 0,
    homePT: 0,
    homeOT: 0,
    homeST: 0,
    homeMSW: 0,
    homeHHA: 0,
    checkSN: true,
    checkPT: true,
    checkOT: true,
    checkST: true,
    checkMSW: true,
    currentStartDate: "",
    currentEndDate: "",
    nextStartDate: "",
    nextEndDate: "",
    nextSN: 0,
    nextPT: 0,
    nextOT: 0,
    nextST: 0,
    nextMSW: 0,
    nextHHA: 0,
    userNameCreated: "",
  },
  authorizationNumber: "",
  icdCodesDetails: {
    typeId: 2,
    fullNameCreated: "",
    icdCodesInfoDto: [],
  },
  requestService: {
    memberAdmitted: "no",
    inpatientAdmitDate: "",
    inpatientDischargeDate: "",
    disciplines: [],
    memberVisit: "no",
    visitDate: "",
    startOfCare: "",
    currentStartDate: "",
    currentEndDate: "",
    nextStartDate: "",
    nextEndDate: "",  
  }
};

// Interfaces for defaultReAuthTabData

export interface IReAuthRequest {
  referralId: number;
  receivedDateTime: string;
  patientEligibilityId: number;
  receivedMethodId: number;
  urgencyId: number;
  userNameCreated: string;
  userEmail: string;
  userPhone: string;
  facilityTypeId: number;
  userCreated: string;
}

export interface IFollowingPhysicianDetail extends IPhysician {
  serviceRequestId: number;
  referralId: number;
  physicianName: string;
  physicianNpiId: string;
  phoneNo: string;
  county: string;
  zipCode: string;
  userNameCreated: string;
  physicianSameAsPrimary: boolean;
  physicianSameAsOrdering: boolean;
}

export interface IReAuthServiceRequest {
  serviceRequestId: number;
  referralId: number;
  startOfCareDate: string;
  homeSN: number;
  homePT: number;
  homeOT: number;
  homeST: number;
  homeMSW: number;
  homeHHA: number;
  checkSN: boolean;
  checkPT: boolean;
  checkOT: boolean;
  checkST: boolean;
  checkMSW: boolean;
  currentStartDate: string;
  currentEndDate: string;
  nextStartDate: string;
  nextEndDate: string;
  nextSN: number;
  nextPT: number;
  nextOT: number;
  nextST: number;
  nextMSW: number;
  nextHHA: number;
  userNameCreated: string;
}

export const notVisitedReAuthTabs = (tabSessionStorage: TabSessionDataReAuthResponse) =>
  tabSessionStorage?.response?.reAuthTabStatus?.filter((tab) => !tab.isVisited) || [];

export interface ReAuthTabData {
  requestId: string;
  userCreated: string;
  dateCreated: string;
  referralId: number;
  noteComment: string;
  fullName: string;
  reAuthRequest: IReAuthRequest;
  followingPhysicianDetail: IFollowingPhysicianDetail;
  reAuthServiceRequest: IReAuthServiceRequest;
  authorizationNumber: string;
  icdCodesDetails: IIcdCodesDetails;
  reAuthTabStatus: TabStatus[];
  reAuthAgencyDetails: IRequester;
  documentUploads: any[];
  diagnosisCode: IDiagnosisCode[];
  requestService: {
    memberAdmitted: string;
    inpatientAdmitDate: string;
    inpatientDischargeDate: string;
    disciplines: any[];
    memberVisit: string;
    visitDate: string;
    startOfCare: string;
    currentStartDate: string;
    currentEndDate: string;
    nextStartDate: string;
    nextEndDate: string;
  };
}


